import styled, { css, keyframes } from "styled-components";

/*
 * Login, u stilu redizajniranih portal stranica:
 * --bg-app pozadina + suptilan brand glow, kartica na --bg-surface
 * (1px var(--border), radius var(--radius-card)), eyebrow + Archivo Black
 * naslov, inputi po SearchField obrascu (fokus = brand ivica, bez glow-a),
 * puno brand dugme. Poštuje prefers-reduced-motion.
 */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(26px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Page = styled.div`
  min-height: 100dvh;
  background: var(--bg-app);
  color: var(--text-primary);
  font-family: var(--font-body);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5.5rem 1.5rem 3rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      560px 380px at 50% 10%,
      rgba(255, 107, 53, 0.1),
      transparent 70%
    );
    pointer-events: none;
  }
`;

export const BackLink = styled.a`
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  z-index: 10;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 40px;
  padding: 0 1rem;
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.18s ease;
  animation: ${fadeUp} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) 0.1s backwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  svg {
    transition: transform 0.18s ease;
  }

  &:hover {
    border-color: var(--brand);
    color: var(--brand);

    svg {
      transform: translateX(-3px);
    }
  }
`;

export const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 2rem;
  animation: ${fadeUp} 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) both;

  /* sadržaj kartice ulazi stepenasto za karticom */
  & > * {
    animation: ${fadeUp} 0.45s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
  }

  ${Array.from(
    { length: 6 },
    (_, i) => css`
      & > *:nth-child(${i + 1}) {
        animation-delay: ${150 + i * 70}ms;
      }
    `,
  )}

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    & > * {
      animation: none;
    }
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1.25rem;
  }
`;

/* Znak + МРЧ logotext, ista kombinacija kao u headeru */
export const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.6rem;

  svg {
    width: 34px;
    height: 34px;
    display: block;
  }

  span {
    font-family: var(--font-display);
    font-size: 1.05rem;
    letter-spacing: 0.04em;
    color: var(--text-primary);
  }
`;

export const Eyebrow = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 0.6rem;
`;

export const Title = styled.h1`
  margin: 0 0 0.55rem;
  font-family: var(--font-display);
  font-size: clamp(30px, 5vw, 40px);
  line-height: 0.95;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);
`;

export const Subtitle = styled.p`
  margin: 0 0 1.6rem;
  color: var(--text-secondary);
  font-size: 0.92rem;
  line-height: 1.55;
`;

export const ErrorMessage = styled.div`
  background: rgba(255, 107, 107, 0.08);
  border: 1px solid rgba(255, 107, 107, 0.35);
  color: var(--danger);
  padding: 0.65rem 0.9rem;
  border-radius: var(--radius-control);
  margin-bottom: 1.1rem;
  font-size: 0.85rem;
`;

export const Field = styled.div`
  margin-bottom: 1rem;
`;

export const FieldLabel = styled.label`
  display: block;
  color: var(--text-secondary);
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.4rem;
`;

export const InputWrap = styled.div`
  position: relative;
`;

export const Input = styled.input`
  width: 100%;
  height: 50px;
  box-sizing: border-box;
  padding: 0 2.9rem 0 0.95rem;
  background: var(--bg-app);
  border: 1px solid
    ${(p) => (p.$error ? "var(--danger)" : "var(--border-strong)")};
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-family: var(--font-body);
  font-size: 0.95rem;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: ${(p) => (p.$error ? "var(--danger)" : "var(--brand)")};
  }
`;

/* Dekorativna ikonica u polju (desno) */
export const InputIcon = styled.span`
  position: absolute;
  right: 0.95rem;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  color: var(--text-muted);
  pointer-events: none;
`;

/* Prikaz/sakrivanje lozinke, pravo dugme zbog tastature/čitača */
export const EyeButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  border-radius: var(--radius-control);
  color: var(--text-muted);
  cursor: pointer;
  transition: color 0.18s ease;

  &:hover {
    color: var(--text-primary);
  }

  &:focus-visible {
    outline: none;
    color: var(--brand);
  }
`;

export const RememberRow = styled.div`
  display: flex;
  align-items: center;
  margin: 0.2rem 0 1.4rem;
`;

export const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.85rem;
  cursor: pointer;
  user-select: none;
  transition: color 0.18s ease;

  input {
    width: 16px;
    height: 16px;
    accent-color: var(--brand);
    cursor: pointer;
  }

  &:hover {
    color: var(--text-primary);
  }
`;

export const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  width: 100%;
  height: 50px;
  background: var(--brand);
  color: #ffffff;
  border: none;
  border-radius: var(--radius-control);
  font-family: var(--font-body);
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
  transition:
    background 0.18s ease,
    transform 0.18s ease;

  &:hover:not(:disabled) {
    background: var(--brand-hover);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SecurityNote = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  margin-top: 1.1rem;
  color: var(--text-muted);
  font-size: 0.78rem;

  svg {
    color: var(--brand);
    flex-shrink: 0;
  }
`;

export const Footer = styled.div`
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border);
  text-align: center;
  color: var(--text-muted);
  font-size: 0.75rem;
`;
